import { useMutation, useQueryClient } from '@tanstack/vue-query'
import fetchApi, { type FetchApiErrorOptions } from '@/utils/fetchApi'
import type { VirtualAgentConfiguration, DispatchMetadata } from 'shared/src'
import { createConfigurationErrorFormatter } from '@/utils/configurationErrorFormatter'

interface SaveConfigurationParams {
  id?: string | null
  body: DispatchMetadata
}

interface SaveConfigurationResult {
  data: VirtualAgentConfiguration
}

const errorOptions: FetchApiErrorOptions = {
  formatter: createConfigurationErrorFormatter(),
  title: 'required_fields',
  message: 'please_fill_in_the_following_section(s)_to_save_:',
}

export function useSaveConfigurationMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, body }: SaveConfigurationParams) => {
      const isUpdate = !!id

      const result = await fetchApi<SaveConfigurationResult>(
        {
          endpoint: isUpdate ? `/api/configurations/update/${id}` : `/api/configurations/create`,
          method: isUpdate ? 'PUT' : 'POST',
          body,
        },
        errorOptions,
      )

      return result
    },
    onSuccess: async (_, { id }) => {
      // Small delay to allow backend indexing
      await new Promise((resolve) => setTimeout(resolve, 500))
      void queryClient.invalidateQueries({ queryKey: ['configurations', 'list'] })
      void queryClient.invalidateQueries({ queryKey: ['configurations', 'languages'] })
      if (id) {
        void queryClient.invalidateQueries({ queryKey: ['configurations', 'data', id] })
      }
    },
  })
}
